/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Dialog, DialogContent, IconButton, LinearProgress, Stack, Typography, Button, Radio, Zoom, Tooltip, Chip } from '@mui/material'
import { useTheme } from '@mui/material/styles';
import ReactHtmlParser from 'react-html-parser';
import { Iconify } from 'src/components'
import { AISummaryModal } from 'src/components/callScriptV2/components';
import { TAB_HEAD } from 'src/constants/LeadDetailConst';
import { useSelector } from 'react-redux';
import { dispatch } from 'src/redux/store';
import { getActivities } from 'src/redux/slices/activity';
import { updateReminder, followUpSlice, getLeadReminderData, leadTaskListCounter, updateTaskCounter, updateLeadTaskCounter } from 'src/redux/slices/lead';
import { CustomAvatar, AssignUserAvatar } from 'src/components/custom-avatar';
import { GetPracticeAvatar } from 'src/utils/createAvatar';
import { callApi } from 'src/utils/apiCall';
import { reminderFields } from 'src/constants/fields';
import moment from 'moment';
import { parseApiDateTime } from 'src/utils/parseApiDateTime';
import capitalizeFirstLetter from 'src/utils/capitalizedFunc';
import { selectChoicesData } from 'src/redux/selectors/dataGridSelectors';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { getMessage } from 'src/utils/getTasksData';
import { POPUP_HEADER_GRADIENT, POPUP_HEADER_SHADOW, POPUP_HEADER_PX, POPUP_HEADER_PY, reminderTitleStyle } from 'src/styles/ScheduleReminder';
import LeadDetailViewBody from 'src/sections/@dashboard/leadDetail/LeadDetailViewBody';
import {
    CALL_OUTCOME_LABEL,
    collapseNoteTaskPairs,
    formatCallDuration,
    getActivityTitle,
    getActivityIcon,
} from 'src/sections/@dashboard/leadDetail/Activities';

// Helper function to check if a note is an AI note
const parseAINote = (noteContent) => {
    if (!noteContent) return null;
    try {
        const parsed = JSON.parse(noteContent);
        if (parsed && parsed.AI_NOTES === true) {
            return parsed;
        }
    } catch (e) {
        // Not a JSON note, return null
    }
    return null;
};

const getNoteDisplayText = (noteContent) => {
    const aiData = parseAINote(noteContent);
    if (aiData) {
        return 'AI Call Summary - Click to view';
    }
    return capitalizeFirstLetter(noteContent);
};

// ── Timeline styles ──
const timelineItem = {
    display: 'flex',
    position: 'relative',
    pb: '22px',
    '&:last-child': { pb: 0 },
};

const timelineLine = {
    position: 'absolute',
    left: '13px',
    top: '32px',
    bottom: 0,
    width: '1px',
    bgcolor: 'rgba(145, 158, 171, 0.12)',
};

const timelineDot = (color = '#919EAB') => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    bgcolor: `${color}14`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    mt: '1px',
});

const timelineContent = {
    flex: 1,
    minWidth: 0,
    ml: '16px',
};

// Typography scale — macOS HIG style (matches Apple Mail / Notes list views).
// Body 13 / caption 11, 3 weights (400/500/600), 3 grays.
const txtBody = { fontSize: '13px', fontWeight: 500, color: '#212B36', lineHeight: 1.45 };
const txtBodyStrong = { fontSize: '13px', fontWeight: 600, color: '#212B36', lineHeight: 1.45 };
const txtBodyMuted = { fontSize: '13px', fontWeight: 400, color: '#637381', lineHeight: 1.45 };
const txtCaption = { fontSize: '11px', fontWeight: 400, color: '#919EAB', lineHeight: 1.4 };
const txtCaptionStrong = { fontSize: '11px', fontWeight: 600, color: '#637381', lineHeight: 1.4 };
const txtSectionLabel = { fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#919EAB', lineHeight: 1.4 };

const statusColors = {
    NEW: { bg: 'rgba(0, 184, 217, 0.08)', color: '#006C9C' },
    CONSULTATION_BOOKED: { bg: 'rgba(142, 51, 255, 0.08)', color: '#5119B7' },
    CONSULTATION_ATTENDED: { bg: 'rgba(255, 193, 7, 0.08)', color: '#B78103' },
    FAILED_TO_ATTEND_CONSULTATION: { bg: 'rgba(255, 72, 66, 0.08)', color: '#B71D18' },
    TREATMENT_STARTED: { bg: 'rgba(84, 214, 44, 0.08)', color: '#229A16' },
    CLOSED: { bg: 'rgba(145, 158, 171, 0.08)', color: '#637381' },
};

const getStatusLabel = (status) => {
    const match = TAB_HEAD.find(e => e.value === status);
    return match?.label || status;
};

// Short, chip-friendly status labels for the timeline status-transition entry —
// "Status: Consultation booked → CONSULTATION_ATTENDED" reduces to a single
// "Attended" chip (the new status). Strips any trailing '.' from the BE message.
const SHORT_STATUS = {
    NEW: 'New',
    CONSULTATION_BOOKED: 'Booked',
    CONSULTATION_ATTENDED: 'Attended',
    FAILED_TO_ATTEND_CONSULTATION: 'No-show',
    TREATMENT_STARTED: 'Started',
    CLOSED: 'Closed',
    ARCHIVED: 'Archived',
};
const cleanStatus = (status) => String(status || '').replace(/\.$/, '').trim().toUpperCase();
const getShortStatusLabel = (status) => {
    const clean = cleanStatus(status);
    return SHORT_STATUS[clean] || getStatusLabel(clean);
};

const getStatusColor = (status) => statusColors[status] || statusColors.CLOSED;

// Parse different date formats to a sortable timestamp.
//
// Delegates to parseApiDateTime (src/utils/parseApiDateTime.js), which reads
// the BE's `DD-MM-YYYY HH:mm:ss.ffffffZ` shape as UK local wall-clock time —
// the trailing 'Z' is a misleading literal, not a real UTC marker. Parsing it
// with a moment format string that ends in a bare 'Z' token (as this function
// used to) makes moment treat that literal 'Z' as a genuine +00:00 offset,
// shifting every timestamp an hour into the future during BST — see that
// module's header comment for the full root-cause explanation of the
// "in an hour" BST bug this fixes.
export const parseEventDate = (dateStr) => {
    if (!dateStr) return 0;
    const m = parseApiDateTime(dateStr);
    return m && m.isValid() ? m.valueOf() : 0;
};

// Format activity timestamp — smart relative for recent, absolute for older.
// Exported for unit testing (see tests/unit/lead/formatActivityTime.OverviewTab.test.js).
export const formatActivityTime = (timestamp) => {
    const m = parseApiDateTime(timestamp);
    if (!m || !m.isValid()) return '';
    const now = moment();
    const diffHours = now.diff(m, 'hours');
    const diffDays = now.diff(m.clone().startOf('day'), 'days');
    if (diffHours < 24 && diffDays === 0) return m.fromNow();
    if (diffDays === 1) return `Yesterday at ${m.format('h:mm a')}`;
    if (diffDays > 1 && diffDays <= 6) return `${m.format('ddd')} at ${m.format('h:mm a')}`;
    if (m.year() === now.year()) return m.format('DD MMM, h:mm a');
    return m.format('DD MMM YYYY, h:mm a');
};

// Date-group bucket label for an event timestamp (in ms).
const getDateGroupLabel = (tsMs) => {
    const m = moment(tsMs);
    if (!m.isValid()) return 'Earlier';
    const today = moment().startOf('day');
    const yesterday = today.clone().subtract(1, 'day');
    const weekStart = today.clone().subtract(6, 'days');
    if (m.isSameOrAfter(today)) return 'Today';
    if (m.isSameOrAfter(yesterday)) return 'Yesterday';
    if (m.isSameOrAfter(weekStart)) return 'This week';
    return 'Earlier';
};

// Outbound contact = something we sent OUT to the lead.
const isOutboundContact = (act) => {
    const v = act?.verb;
    if (v === 'message_sent' || v === 'whatsapp_message_sent') return true;
    if (v === 'note_added') {
        const noteType = act?.action_object?.note;
        return noteType === 'FOLLOW_UP' || noteType === 'AWAITING_RESPONSE';
    }
    return false;
};

// Activity verb → icon + color mapping.
// NOTE: hex literals (not palette tokens) — colours flow through `${color}1F`
// substring interpolations elsewhere; theme tokens won't resolve there.
const verbConfig = {
    created: { icon: 'solar:user-plus-bold', color: '#00A76F' },
    treatment_added: { icon: 'solar:teeth-bold', color: '#078DEE' },
    lead_treatment_status_transitioned: { icon: 'eva:swap-fill', color: '#FFAB00' },
    note_added: { icon: 'solar:pen-bold', color: '#FF5630' },
    message_sent: { icon: 'ic:round-email', color: '#078DEE' },
    whatsapp_message_sent: { icon: 'ic:baseline-whatsapp', color: '#25D366' },
    appointment_booked: { icon: 'solar:calendar-bold', color: '#5119B7' },
    appointment_attended: { icon: 'eva:checkmark-circle-2-fill', color: '#00A76F' },
    appointment_failed: { icon: 'eva:close-circle-fill', color: '#FF5630' },
    task_checklist_next_action: { icon: 'solar:checklist-minimalistic-bold', color: '#5119B7' },
    task_due_date_changed: { icon: 'solar:clock-circle-bold', color: '#5119B7' },
    task_title_changed: { icon: 'solar:pen-bold', color: '#5119B7' },
    default: { icon: 'solar:bell-bold', color: '#919EAB' },
};

const getVerbConfig = (verb) => verbConfig[verb] || verbConfig.default;

// WhatsApp delivery status config
const whatsappStatusConfig = {
    read: { label: 'Read', color: '#078DEE', icon: 'eva:done-all-fill' },
    delivered: { label: 'Delivered', color: '#919EAB', icon: 'eva:done-all-fill' },
    sent: { label: 'Sent', color: '#C4CDD5', icon: 'mdi:check' },
    failed: { label: 'Failed', color: '#FF5630', icon: 'eva:close-fill' },
};

// Should this activity be filtered out?
const shouldFilterActivity = (act) => {
    const verb = act?.verb;
    const msg = act?.data?.data?.message || '';
    const actionObj = act?.action_object;

    // Filter internal notification emails ("New whatsapp message from...")
    if (verb === 'message_sent' && actionObj?.message_category === 'NOTIFICATION') return true;

    // Filter NEW→NEW status transitions (noise from lead creation)
    if (verb === 'lead_treatment_status_transitioned' && msg.includes('NEW to NEW')) return true;

    // Filter duplicate treatment auto-removals
    if (verb === 'duplicate_treatment_auto_removed') return true;

    return false;
};

// Consolidate near-simultaneous creation events
const consolidateCreationEvents = (activities) => {
    if (!activities?.length) return activities;

    // Find 'created' events and determine which IDs to absorb
    const absorbedIds = new Set();
    const createdEvents = activities.filter(a => a.verb === 'created');

    createdEvents.forEach((created) => {
        const createdTime = parseEventDate(created.timestamp);
        activities.forEach((other) => {
            if (other.id === created.id) return;
            const otherTime = parseEventDate(other.timestamp);
            if (Math.abs(createdTime - otherTime) < 5000
                && (other.verb === 'treatment_added' || other.verb === 'lead_treatment_status_transitioned')) {
                absorbedIds.add(other.id);
            }
        });
    });

    // Deduplicate same-second note_added events (keep the one with additional_notes)
    const deduped = activities.filter(a => !absorbedIds.has(a.id));
    const result = [];

    deduped.forEach((act) => {
        if (act.verb === 'note_added') {
            const actTime = act.timestamp?.substring(0, 19);
            const existing = result.find(
                r => r.verb === act.verb && r.timestamp?.substring(0, 19) === actTime
                    && r.action_object?.note === act.action_object?.note
            );
            if (existing) {
                // Keep the one with additional_notes
                if (act.action_object?.additional_notes && !existing.action_object?.additional_notes) {
                    const idx = result.indexOf(existing);
                    result[idx] = act;
                }
                return;
            }
        }
        result.push(act);
    });

    return result;
};


// Verbs whose label AND icon are delegated to the shared Activities helpers
// (`getActivityTitle` / `getActivityIcon`): task-checklist channel verbs +
// consent/payment verbs + call_logged. These previously fell through to the
// generic capitalizeFirstLetter(msg) → "Other" and the gray default bell icon.
// IMPORTANT: the label (getActivityTitle) and icon (getActivityIcon) delegation
// MUST stay in sync — this single set drives both the label path
// (`resolveActivityLabel`) and the icon path (`getActivityVerbIcon` singleton +
// `renderActivityGroup` expanded). note=OTHER is delegated too, but as a
// note_added SUB-TYPE check (not a verb) in each path — not listed here.
const DELEGATED_ACTIVITY_VERBS = new Set([
    'task_checklist_whatsapp_sent',
    'task_checklist_email_sent',
    'task_checklist_sms_sent',
    'task_checklist_phone_called',
    'task_consent_form_sent',
    'task_payment_link_sent',
    'task_payment_manually_acknowledged',
    'call_logged',
]);

/**
 * Derives the correct display label for contact-action activities that previously
 * rendered as "Other" or a generic verb-only string in the FollowUp V2 Overview grouped
 * timeline rows.  Delegates to the shared Activities helper (`getActivityTitle`) which
 * already resolves contacted_via / call_outcome / TASK_VERB_TITLE correctly.
 *
 * Returns `null` for verbs that are handled by OverviewTab-specific logic in
 * `getActivityLabel` — callers should fall through to that logic when null is returned.
 *
 * Exported so unit tests can assert label mapping without mounting the component.
 */
export const resolveActivityLabel = (act) => {
    const verb = act?.verb;
    // note_added + note=OTHER: derive title from contacted_via / call_outcome fields.
    if (verb === 'note_added' && act?.action_object?.note === 'OTHER') {
        return getActivityTitle(act);
    }
    // Task-checklist channel verbs + ancillary task verbs + call_logged
    // that previously fell through to the generic capitalizeFirstLetter(msg) → "Other".
    if (DELEGATED_ACTIVITY_VERBS.has(verb)) return getActivityTitle(act);
    return null;
};

const OverviewTab = ({ treatmentTabData, leadId, handleChange, user, LeadNotes, showDetailModes, topSlot }) => {
    useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const { practiceTreatment, practiceId } = useSelector((state) => state.practiceTreatmentList);
    const [detailDialog, setDetailDialog] = useState(null); // 'popup' | 'fullscreen' | null
    const { activityList: rawActivityList } = useSelector((state) => state.activity);
    // GDPR guard: activityList is a single GLOBAL slice shared by every surface. Only
    // surface activities that belong to the CURRENT lead — otherwise the previously
    // viewed lead's timeline leaks through while/after this lead's fetch (RCA
    // 2026-06-19). Shadowing the name means every consumer below is guarded for free.
    const activityList = String(rawActivityList?._leadId) === String(leadId)
        ? rawActivityList
        : { results: [], count: 0 };
    const { leadFlowReminder, followLead } = useSelector((state) => state.lead);
    const activeFollowLead = followLead && (followLead.id === leadId) ? followLead : null;
    const choicesData = useSelector(selectChoicesData);

    const [completedTasks, setCompletedTasks] = useState([]);
    const [markComplete, setMarkComplete] = useState(false);
    const [editingPrice, setEditingPrice] = useState(null);
    const [priceValue, setPriceValue] = useState('');
    const [expandedActivity, setExpandedActivity] = useState(null);

    const checklistSummary = useMemo(() => {
        if (!choicesData?.length || !activeFollowLead?.lead_treatments?.length) return null;
        const treatment = activeFollowLead.lead_treatments[0];
        const attrs = treatment?.lead_treatment_attributes || [];
        const meta = activeFollowLead?.lead_metadata || [];
        const items = [];
        choicesData.forEach((choice) => {
            if (
                choice?.metadata?.tables?.includes('Attended Checklists')
                || choice?.metadata?.tables?.includes('Consultation Attended Leads')
            ) {
                items.push({ key: choice.name, label: choice.name });
            }
        });
        if (!items.length) return null;
        const completed = [];
        const pending = [];
        items.forEach((item) => {
            const attr = attrs.find((a) => a.key === item.key);
            let val = attr?.data?.[0]?.value || '';
            if (!val) {
                const m = meta.find((x) => x.key === item.key);
                val = m?.value || '';
            }
            if (val && val.trim() !== '') {
                completed.push({ ...item, value: val, updatedAt: attr?.updated_at || null });
            } else {
                pending.push(item);
            }
        });
        return { items, completed, pending, total: items.length };
    }, [choicesData, activeFollowLead]);

    const [aiSummaryModalOpen, setAISummaryModalOpen] = useState(false);
    const [selectedAIData, setSelectedAIData] = useState(null);

    const handleOpenAISummary = useCallback((aiData) => {
        setSelectedAIData(aiData);
        setAISummaryModalOpen(true);
    }, []);

    const handleCloseAISummary = useCallback(() => {
        setAISummaryModalOpen(false);
        setSelectedAIData(null);
    }, []);

    useEffect(() => {
        if (leadId) {
            // Match the full lead-detail timeline's first page. The sidebar is
            // compact, but it must not silently omit the patient's recent
            // journey or checklist notes after only 20 activity records.
            dispatch(getActivities(practiceId, leadId, 100, 0));
        }
    }, [leadId, practiceId]);

    // Refresh the timeline when a task changes in this popup (completed / paused /
    // rescheduled / renamed). The task section broadcasts `lead-tasks-v2-changed`;
    // without this the popup's timeline only loaded once on open and never reflected
    // the just-completed task.
    useEffect(() => {
        if (!leadId) return undefined;
        const refetch = () => dispatch(getActivities(practiceId, leadId, 100, 0));
        window.addEventListener('lead-tasks-v2-changed', refetch);
        return () => window.removeEventListener('lead-tasks-v2-changed', refetch);
    }, [leadId, practiceId]);

    // Fetch completed tasks
    useEffect(() => {
        // Clear the previous lead's completed tasks immediately so they can't leak
        // into this lead's view during the fetch window (GDPR — RCA 2026-06-19).
        setCompletedTasks([]);
        if (practiceId && leadId) {
            const fetchCompleted = async () => {
                const fields = `&fields=${reminderFields}`;
                const res = await callApi(`/api/v1/practices/${practiceId}/reminders/?group__in=TODO,FOLLOW_UP,CALLBACK_REQUEST&is_completed=true&lead=${leadId}&ordering=-datetime&limit=10&offset=0${fields}&lead__is_archived=false`);
                if (res?.status === 200) {
                    setCompletedTasks(res.data?.results || []);
                }
            };
            fetchCompleted();
        }
    }, [practiceId, leadId]);

    // Task mark complete handler
    const handleReminder = (value) => {
        dispatch(updateTaskCounter([]));
        if (value === 'markComplete' && user) {
            dispatch(updateLeadTaskCounter([]));
            dispatch(getLeadReminderData(practiceId, 'LeadDetail', 10, 0, 'NEW', user?.lead_id || '', '', '', 'leadNotes', 'counter', 'All', undefined, true));
            dispatch(leadTaskListCounter(practiceId, 'LeadDetail', user?.status, user?.lead_id, '', '', undefined, true));
            dispatch(followUpSlice(user?.lead_id, practiceId, 'LeadDetail'));
        }
    };

    const markCompleteBtn = (value) => {
        setMarkComplete(value);
        dispatch(updateReminder({ is_completed: true }, value, practiceId, handleReminder, 'markComplete', (data) => {
            setMarkComplete(false);
            enqueueSnackbar(data.message || '', {
                variant: data.success ? 'success' : 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
        }));
    };

    // ── Build unified event stream ──
    const eventStream = useMemo(() => {
        const events = [];

        // Activities — filter noise, consolidate creation events
        if (Array.isArray(activityList?.results)) {
            const filtered = activityList.results.filter(act => !shouldFilterActivity(act));
            // The full lead-detail timeline collapses checklist actions with their
            // matching audit note. Use the same pass here so both views retain the
            // call outcome and operator note on the surviving checklist event.
            const consolidated = collapseNoteTaskPairs(consolidateCreationEvents(filtered));
            consolidated.forEach((act) => {
                events.push({
                    type: 'activity',
                    date: parseEventDate(act.timestamp),
                    data: act,
                });
            });
        }

        // Pending tasks
        if (Array.isArray(leadFlowReminder?.results)) {
            leadFlowReminder.results.forEach((task) => {
                events.push({
                    type: 'task',
                    isCompleted: false,
                    date: parseEventDate(task.datetime),
                    data: task,
                });
            });
        }

        // Completed tasks
        if (Array.isArray(completedTasks)) {
            completedTasks.forEach((task) => {
                events.push({
                    type: 'task',
                    isCompleted: true,
                    date: parseEventDate(task.datetime),
                    data: task,
                });
            });
        }

        // Notes — skip notes already represented as activity events
        if (Array.isArray(LeadNotes)) {
            // Collect note IDs from activity action_objects to avoid duplicates
            const activityNoteIds = new Set();
            if (Array.isArray(activityList?.results)) {
                activityList.results.forEach((act) => {
                    if (act.verb === 'note_added' && act.action_object?.id) {
                        activityNoteIds.add(act.action_object.id);
                    }
                });
            }
            LeadNotes.forEach((note) => {
                // Skip notes already shown as activities
                if (note?.id && activityNoteIds.has(note.id)) return;
                events.push({
                    type: 'note',
                    date: parseEventDate(note?.updated_at),
                    data: note,
                });
            });
        }

        // Sort newest first
        events.sort((a, b) => b.date - a.date);
        return events;
    }, [activityList, leadFlowReminder, completedTasks, LeadNotes]);

    // ── Group consecutive "action burst" activities into one expandable row ──
    // When a user fires several actions in one go (email + WhatsApp + logged call
    // as part of one next-action), the timeline would otherwise show ~4 separate
    // rows. Cluster a maximal run of consecutive ACTION_VERBS activities by the
    // same actor within GROUP_WINDOW_MS of the run's first event.
    const groupedStream = useMemo(() => {
        const ACTION_VERBS = new Set([
            'message_sent',
            'whatsapp_message_sent',
            'task_checklist_next_action',
            'task_checklist_email_sent',
            'task_checklist_whatsapp_sent',
            'task_checklist_phone_called',
            'note_added',
        ]);
        const GROUP_WINDOW_MS = 120000;

        const isActionEvent = (ev) => ev?.type === 'activity' && ACTION_VERBS.has(ev?.data?.verb);

        const result = [];
        let i = 0;
        while (i < eventStream.length) {
            const ev = eventStream[i];
            if (!isActionEvent(ev)) {
                result.push(ev);
                i += 1;
            } else {
                // Start a run anchored on this action event.
                const firstEv = ev;
                const firstActorId = firstEv.data?.actor?.id;
                const run = [firstEv];
                let j = i + 1;
                let extend = true;
                while (extend && j < eventStream.length) {
                    const next = eventStream[j];
                    if (
                        !isActionEvent(next) ||
                        next.data?.actor?.id !== firstActorId ||
                        Math.abs(firstEv.date - next.date) > GROUP_WINDOW_MS
                    ) {
                        extend = false;
                    } else {
                        run.push(next);
                        j += 1;
                    }
                }

                if (run.length >= 2) {
                    result.push({
                        type: 'activity-group',
                        date: firstEv.date,
                        actor: firstEv.data?.actor,
                        items: run.map((r) => r.data),
                    });
                } else {
                    result.push(firstEv);
                }
                i = j;
            }
        }
        return result;
    }, [eventStream]);

    // Most recent outbound contact id — marks one row with a subtle ring
    // so the user can spot "where I left off" without scrolling.
    const latestOutboundId = useMemo(() => {
        const items = activityList?.results || [];
        const found = items.find((it) => isOutboundContact(it));
        return found ? (found.id || null) : null;
    }, [activityList?.results]);

    // ── Render helpers ──
    const getActivityLabel = (act) => {
        const verb = act?.verb;
        const msg = act?.data?.data?.message || '';
        const actionObj = act?.action_object;

        switch (verb) {
            case 'created':
                return 'Lead created';
            case 'treatment_added':
                return `${capitalizeFirstLetter(msg.replace(' treatment added.', ''))} added`;
            case 'lead_treatment_status_transitioned': {
                // Extract status names from message like "from X to Y"
                const match = msg.match(/from\s+(\S+)\s+to\s+(\S+)/i);
                if (match) {
                    return `Status: ${getStatusLabel(match[1]?.trim())} → ${getStatusLabel(match[2]?.trim())}`;
                }
                return capitalizeFirstLetter(msg);
            }
            case 'task_checklist_next_action': {
                // "Next action recorded (task checklist: X)" / "...from task checklist: X" → "Task: X"
                const m = msg.match(/task checklist:\s*(.+)$/i);
                const title = m?.[1]?.replace(/\)\s*$/, '').trim();
                return title ? `Task: ${title}` : 'Task';
            }
            case 'task_due_date_changed': {
                // "Due date updated for task: X" → "Rescheduled: X"
                const m = msg.match(/for task:\s*(.+)$/i);
                const title = m?.[1]?.trim();
                return title ? `Rescheduled: ${title}` : 'Task rescheduled';
            }
            case 'task_title_changed': {
                // "Task renamed: \"A\" → \"B\"" → "Renamed: B"
                const m = msg.match(/→\s*"?([^"]+?)"?\.?\s*$/);
                const newName = m?.[1]?.trim();
                return newName ? `Renamed: ${newName}` : 'Task renamed';
            }
            case 'note_added': {
                if (actionObj?.note === 'UNABLE_TO_CONTACT') {
                    const via = actionObj?.contacted_via ? ` via ${capitalizeFirstLetter(actionObj.contacted_via.toLowerCase())}` : '';
                    const extra = actionObj?.additional_notes ? ` — ${actionObj.additional_notes}` : '';
                    return `Unable to contact${via}${extra}`;
                }
                if (actionObj?.note === 'FOLLOW_UP') {
                    const extra = actionObj?.additional_notes ? ` — ${actionObj.additional_notes}` : '';
                    return `Follow up${extra}`;
                }
                if (actionObj?.note === 'AWAITING_RESPONSE') return 'Waiting/Scheduled';
                if (msg === 'Awaiting Response.') return 'Waiting/Scheduled';
                // note=OTHER and any other unhandled sub-type: derive from structured fields.
                return resolveActivityLabel(act) ?? capitalizeFirstLetter(msg);
            }
            case 'message_sent': {
                const subject = actionObj?.subject || '';
                const isAuto = actionObj?.message_group === 'SYSTEM' || actionObj?.message_category === 'SEQUENCE_MESSAGE';
                const prefix = isAuto ? 'Auto email' : 'Email sent';
                if (subject && !subject.startsWith('$')) {
                    return `${prefix}: ${subject}`;
                }
                return prefix;
            }
            case 'whatsapp_message_sent': {
                // Extract first ~80 chars of the actual message
                const rawMsg = msg.replace('Whatsapp message sent - ', '');
                const truncated = rawMsg.length > 80 ? `${rawMsg.substring(0, 80)}...` : rawMsg;
                return truncated;
            }
            default: {
                // Delegate task-checklist + call_logged verbs to the Activities helper.
                const delegated = resolveActivityLabel(act);
                return delegated !== null ? delegated : capitalizeFirstLetter(msg);
            }
        }
    };

    const getActivityVerbIcon = (act) => {
        const verb = act?.verb;
        // note_added: delegate entirely to the Activities helper which handles all
        // sub-types (UNABLE_TO_CONTACT, FOLLOW_UP, AWAITING_RESPONSE) AND note=OTHER
        // (contacted_via → channel icon).  This replaces the previous per-sub-type
        // manual mapping that left note=OTHER with a generic pen icon.
        if (verb === 'note_added') return getActivityIcon(act);
        // Auto emails get a different shade
        if (verb === 'message_sent') {
            const isAuto = act?.action_object?.message_group === 'SYSTEM' || act?.action_object?.message_category === 'SEQUENCE_MESSAGE';
            if (isAuto) return { icon: 'mdi:email-fast-outline', color: '#919EAB' };
            return { icon: 'ic:round-email', color: '#078DEE' };
        }
        // call_logged + task-checklist/consent/payment channel verbs: delegate to the
        // Activities icon helper so their icon reflects the channel (WhatsApp/Email/
        // SMS/phone-outcome) in the SINGLETON render path too — matching the grouped
        // path and the label delegation. Uses the same shared DELEGATED_ACTIVITY_VERBS
        // set as the label so both stay in sync. getActivityIcon returns { icon, color }
        // (same shape getVerbConfig produces below).
        if (DELEGATED_ACTIVITY_VERBS.has(verb)) return getActivityIcon(act);
        const config = getVerbConfig(verb);
        return { icon: config.icon, color: config.color };
    };

    // Checklist actions can carry the outcome/notes from their paired audit
    // note under these private fields (see collapseNoteTaskPairs). Keep this
    // resolver local to the renderer so normal notes and collapsed task notes
    // are presented identically in the popup.
    const getActivityDetails = (act) => {
        const action = act?.action_object || {};
        const outcome = action.call_outcome || act?._callOutcome || null;
        const duration = outcome ? formatCallDuration(action.duration || act?._callDuration) : '';
        const notes = action.additional_notes || act?._additionalNotes || '';
        return { outcome, duration, notes };
    };

    // Get delivery status config for messages (WhatsApp or Email)
    const getMessageStatusConfig = (act) => {
        const verb = act?.verb;
        const actionObj = act?.action_object;

        // WhatsApp delivery status
        if (verb === 'whatsapp_message_sent') {
            const status = actionObj?.status || 'sent';
            return whatsappStatusConfig[status] || whatsappStatusConfig.sent;
        }

        // Email delivery status — check sent_messages event_counts for delivery tracking
        if (verb === 'message_sent' && actionObj?.type === 'EMAIL') {
            if (actionObj?.message_category === 'NOTIFICATION') return null;

            // Check sent_messages event_counts for delivery tracking
            const eventCounts = actionObj?.sent_messages?.flatMap(sm => sm?.event_counts || []) || [];
            const hasEvent = (eventName) => eventCounts.some(ec => ec?.event === eventName && ec?.count > 0);

            if (hasEvent('clicked')) return { label: 'Clicked', color: '#00A76F', icon: 'mdi:cursor-default-click' };
            if (hasEvent('opened')) return { label: 'Opened', color: '#078DEE', icon: 'mdi:email-open' };
            if (hasEvent('delivered')) return { label: 'Delivered', color: '#919EAB', icon: 'eva:done-all-fill' };

            // Fallback
            const isAuto = actionObj?.message_group === 'SYSTEM' || actionObj?.message_category === 'SEQUENCE_MESSAGE';
            if (isAuto) return { label: 'Auto', color: '#C4CDD5', icon: 'mdi:robot-outline' };
            return { label: 'Sent', color: '#919EAB', icon: 'mdi:check' };
        }

        return null;
    };

    const renderActivity = (act, isLast, isLatest) => {
        const verb = act?.verb;
        const { icon, color } = getActivityVerbIcon(act);
        const actorName = act?.actor?.first_name && act?.actor?.first_name.trim() ? `${act.actor.first_name} ${act.actor.last_name || ''}`.trim() : '';
        const timeStr = formatActivityTime(act.timestamp);
        const isWhatsApp = verb === 'whatsapp_message_sent';
        const statusConfig = getMessageStatusConfig(act);
        const { outcome, duration, notes } = getActivityDetails(act);

        return (
            <Box key={`activity-${act.id}`} sx={timelineItem}>
                {!isLast && <Box sx={timelineLine} />}
                <Box sx={{ position: 'relative', flexShrink: 0, alignSelf: 'flex-start', width: 28, height: 28, mt: '1px' }}>
                    {isLatest && (
                        <Tooltip title="Most recent contact" placement="top">
                            <Box sx={{
                                position: 'absolute',
                                inset: -2,
                                borderRadius: '50%',
                                border: `1.5px solid ${color}`,
                                opacity: 0.55,
                                pointerEvents: 'none',
                            }} />
                        </Tooltip>
                    )}
                    <Box sx={{ ...timelineDot(color), mt: 0 }}>
                        <Iconify icon={icon} width={14} height={14} sx={{ color }} />
                    </Box>
                </Box>
                <Box sx={timelineContent}>
                    {(() => {
                        const isExpandable = isWhatsApp || verb === 'message_sent';
                        const isExpanded = expandedActivity === act.id;
                        const toggle = () => setExpandedActivity(isExpanded ? null : act.id);
                        // Status transition → a single short status chip (the new status),
                        // replacing the verbose "Status: X → Y" text. Falls through to the
                        // text label only if the message can't be parsed.
                        if (verb === 'lead_treatment_status_transitioned') {
                            const m = (act?.data?.data?.message || '').match(/to\s+([A-Z_]+)\.?\s*$/i);
                            if (m?.[1]) {
                                const sc = getStatusColor(cleanStatus(m[1]));
                                return (
                                    <Chip
                                        size="small"
                                        label={getShortStatusLabel(m[1])}
                                        sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, bgcolor: sc.bg, color: sc.color, borderRadius: '6px', '& .MuiChip-label': { px: 1 } }}
                                    />
                                );
                            }
                        }
                        return (
                            <Typography
                                component={isExpandable ? 'div' : 'p'}
                                role={isExpandable ? 'button' : undefined}
                                tabIndex={isExpandable ? 0 : undefined}
                                aria-expanded={isExpandable ? isExpanded : undefined}
                                sx={{
                                    ...txtBody,
                                    // Quieter timeline rows — a notch smaller + lighter than the
                                    // shared txtBody so the activity text doesn't read heavy.
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    background: 'transparent',
                                    border: 'none',
                                    padding: 0,
                                    textAlign: 'left',
                                    display: 'block',
                                    width: '100%',
                                    outline: 'none',
                                    margin: 0,
                                    ...(isExpandable && {
                                        whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        cursor: 'pointer',
                                        '&:focus-visible': {
                                            boxShadow: '0 0 0 2px rgba(24,144,255,0.25)',
                                            borderRadius: '4px',
                                        },
                                    }),
                                }}
                                onClick={isExpandable ? toggle : undefined}
                                onKeyDown={isExpandable ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggle();
                                    }
                                } : undefined}
                            >
                                {getActivityLabel(act)}
                            </Typography>
                        );
                    })()}
                    {(outcome || notes) && (
                        <Box sx={{ mt: '4px', display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            {outcome && (
                                <Typography sx={{ ...txtCaptionStrong, color: '#637381' }}>
                                    {CALL_OUTCOME_LABEL[outcome] || outcome}{duration ? ` · ${duration}` : ''}
                                </Typography>
                            )}
                            {notes && (
                                <Tooltip title={notes} placement="bottom">
                                    <Typography sx={{ ...txtCaption, fontStyle: 'italic', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        “{notes}”
                                    </Typography>
                                </Tooltip>
                            )}
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: '4px' }}>
                        {actorName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CustomAvatar
                                    src={act?.actor?.avatar || GetPracticeAvatar(practiceTreatment, practiceId)[0]?.logo}
                                    name={act?.actor?.first_name}
                                    color="default"
                                    sx={{ width: 16, height: 16, fontSize: '0.55rem' }}
                                />
                                <Typography sx={txtCaption}>{actorName}</Typography>
                            </Box>
                        )}
                        {timeStr && <Typography sx={txtCaption}>{actorName ? '·' : ''} {timeStr}</Typography>}
                        {statusConfig && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', ml: 'auto' }}>
                                <Iconify icon={statusConfig.icon} width={12} height={12} sx={{ color: statusConfig.color }} />
                                <Typography sx={{ ...txtCaption, fontWeight: 600, color: statusConfig.color }}>{statusConfig.label}</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderActivityGroup = (group, isLast) => {
        const actor = group?.actor;
        const actorName = actor?.first_name && actor.first_name.trim()
            ? `${actor.first_name} ${actor.last_name || ''}`.trim()
            : '';
        const count = group?.items?.length || 0;
        const groupKey = `grp-${group.date}`;
        const isExpanded = expandedActivity === groupKey;
        const toggle = () => setExpandedActivity(isExpanded ? null : groupKey);
        const footerTime = formatActivityTime(group?.items?.[0]?.timestamp || group.date);
        const summary = actorName ? `${actorName} — ${count} actions` : `${count} actions`;

        return (
            <Box key={groupKey} sx={timelineItem}>
                {!isLast && <Box sx={timelineLine} />}
                <Box sx={{ ...timelineDot('#5119B7'), mt: 0 }}>
                    <Iconify icon="solar:layers-minimalistic-bold" width={14} height={14} sx={{ color: '#5119B7' }} />
                </Box>
                <Box sx={timelineContent}>
                    <Box
                        component="button"
                        type="button"
                        onClick={toggle}
                        aria-expanded={isExpanded}
                        sx={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            margin: 0,
                            cursor: 'pointer',
                            outline: 'none',
                            '&:focus-visible': {
                                boxShadow: '0 0 0 2px rgba(24,144,255,0.25)',
                                borderRadius: '4px',
                            },
                        }}
                    >
                        <Typography sx={{ ...txtBody, fontSize: '12px', fontWeight: 400 }}>
                            {summary}
                        </Typography>
                        {!isExpanded && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: '6px' }}>
                                {group.items.map((item, ci) => (
                                    <Chip
                                        key={`${groupKey}-chip-${item?.id || ci}`}
                                        size="small"
                                        label={getActivityLabel(item)}
                                        sx={{
                                            height: 20,
                                            fontSize: '0.68rem',
                                            bgcolor: 'rgba(145,158,171,0.12)',
                                            color: '#637381',
                                            borderRadius: '6px',
                                            maxWidth: '100%',
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                    {isExpanded && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: '6px' }}>
                            {group.items.map((item, ci) => {
                                // Use the richer Activities icon helper for verbs where
                                // getVerbConfig returns a generic result (task_checklist_*
                                // channel verbs, consent/payment, call_logged — the shared
                                // DELEGATED_ACTIVITY_VERBS set) plus note_added (which
                                // delegates by note sub-type). getActivityIcon returns
                                // { icon, color }, same shape as getVerbConfig. Fall back to
                                // getVerbConfig for all other verbs.
                                const cfg = (DELEGATED_ACTIVITY_VERBS.has(item?.verb) || item?.verb === 'note_added')
                                    ? getActivityIcon(item)
                                    : getVerbConfig(item?.verb);
                                const detail = getActivityDetails(item);
                                return (
                                    <Box key={`${groupKey}-item-${item?.id || ci}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                        <Iconify icon={cfg.icon} width={12} height={12} sx={{ color: cfg.color, flexShrink: 0 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ ...txtBody, fontSize: '12px', fontWeight: 400 }}>
                                                {getActivityLabel(item)}
                                            </Typography>
                                            {(detail.outcome || detail.notes) && (
                                                <Typography sx={{ ...txtCaption, fontStyle: detail.notes ? 'italic' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {detail.outcome ? `${CALL_OUTCOME_LABEL[detail.outcome] || detail.outcome}${detail.duration ? ` · ${detail.duration}` : ''}` : ''}
                                                    {detail.outcome && detail.notes ? ' · ' : ''}
                                                    {detail.notes ? `“${detail.notes}”` : ''}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography sx={{ ...txtCaption, flexShrink: 0 }}>
                                            {formatActivityTime(item?.timestamp)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: '4px' }}>
                        {actorName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CustomAvatar
                                    src={actor?.avatar || GetPracticeAvatar(practiceTreatment, practiceId)[0]?.logo}
                                    name={actor?.first_name}
                                    color="default"
                                    sx={{ width: 16, height: 16, fontSize: '0.55rem' }}
                                />
                                <Typography sx={txtCaption}>{actorName}</Typography>
                            </Box>
                        )}
                        {footerTime && <Typography sx={txtCaption}>{actorName ? '·' : ''} {footerTime}</Typography>}
                    </Box>
                </Box>
            </Box>
        );
    };

    const getTaskTimeColor = (task, completed) => {
        if (completed) return '#919EAB';
        if (task?.datetime_human?.includes('now' || 'NOW')) return '#00A76F';
        return '#FF5630';
    };

    const renderTask = (task, completed, isLast) => {
        const assigneeData = task?.assignees?.filter((el) => el?.user?.first_name && !el?.user?.is_superuser);
        return (
            <Box key={`task-${task.id}`} sx={{ ...timelineItem, opacity: completed ? 0.55 : 1 }}>
                {!isLast && <Box sx={timelineLine} />}
                {completed ? (
                    <Box sx={timelineDot('#00A76F')}>
                        <Iconify icon="eva:checkmark-circle-2-fill" width={14} height={14} sx={{ color: '#00A76F' }} />
                    </Box>
                ) : (
                    <Box sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '1px' }}>
                        <Tooltip title={task.id === markComplete ? 'Mark as incomplete' : 'Mark as complete'} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                            <Radio
                                checked={task.id === markComplete}
                                onClick={() => markCompleteBtn(task.id)}
                                name='radioButtons'
                                sx={{ p: 0, 'svg': { width: '18px', height: '18px' } }}
                            />
                        </Tooltip>
                    </Box>
                )}
                <Box sx={timelineContent}>
                    <Typography sx={{ ...(completed ? { ...txtBody, color: '#919EAB', textDecoration: 'line-through' } : txtBodyStrong) }} noWrap>
                        {task.title !== '' ? capitalizeFirstLetter(task?.title) : getMessage(task?.lead_treatment?.status, task?.lead?.first_name, task?.lead?.last_name, task?.lead_treatment?.treatment, task?.group, task?.method, task?.note)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '4px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', ...txtCaption, fontWeight: 600, color: getTaskTimeColor(task, completed) }}>
                            <Iconify icon={'mdi:clock-time-three-outline'} width={12} height={12} sx={{ mr: '3px' }} />
                            {task.datetime_human || ''}
                        </Box>
                        {assigneeData?.length > 0 && (
                            <Box sx={{ display: 'flex', ml: 'auto' }}>
                                {assigneeData.slice(0, 2).map((assignee, index) => (
                                    <AssignUserAvatar
                                        key={index}
                                        sx={{ height: '18px', width: '18px', fontSize: '0.6rem', border: '1px solid #fff', '&:nth-child(2)': { ml: '-4px' } }}
                                        src={assignee.user?.avatar || ''}
                                        name={`${assignee.user?.first_name || ''} ${assignee.user?.last_name || ''}`}
                                        role={assignee.role}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderNote = (note, isLast) => {
        const aiNoteData = parseAINote(note?.additional_notes);
        const noteText = note?.additional_notes || note?.note || note?.lead_contact?.note || '';
        return (
            <Box key={`note-${note.id || note?.updated_at}`} sx={timelineItem}>
                {!isLast && <Box sx={timelineLine} />}
                <Box sx={timelineDot('#7635dc')}>
                    <Iconify icon="solar:document-text-bold" width={14} height={14} sx={{ color: '#7635dc' }} />
                </Box>
                <Box sx={timelineContent}>
                    {aiNoteData ? (
                        <Button
                            variant="text"
                            startIcon={<Iconify icon="mdi:robot-outline" width={14} />}
                            onClick={() => handleOpenAISummary(aiNoteData)}
                            sx={{
                                ...txtBodyStrong, color: '#7635dc', textTransform: 'none',
                                p: 0, minWidth: 'auto',
                                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                            }}
                        >
                            View AI Call Summary
                        </Button>
                    ) : (
                        <Typography sx={txtBodyMuted} noWrap>
                            {getNoteDisplayText(noteText)}
                        </Typography>
                    )}
                    <Typography sx={{ ...txtCaption, mt: '4px' }}>
                        {formatActivityTime(note?.updated_at)}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const hasChecklist = activeFollowLead?.lead_treatments?.some(t => ['CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'TREATMENT_STARTED_INVISALIGN', 'CLOSED'].includes(t.status)) && checklistSummary && checklistSummary.total > 0;
    const hasEvents = eventStream.length > 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: '10px 20px', borderBottom: '1px solid rgba(145, 158, 171, 0.08)',
            }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#212B36' }}>Overview</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {showDetailModes && (
                        <>
                            <Tooltip title="Open in popup" TransitionComponent={Zoom} TransitionProps={{ timeout: 200 }}>
                                <IconButton
                                    size="small"
                                    aria-label="Open in popup"
                                    data-testid="lead-detail-open-popup"
                                    onClick={() => setDetailDialog('popup')}
                                    sx={{ color: '#919EAB', '&:hover': { color: '#637381', bgcolor: 'rgba(145,158,171,0.08)' } }}
                                >
                                    <Iconify icon="mdi:rectangle-outline" width={18} height={18} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Open in full screen" TransitionComponent={Zoom} TransitionProps={{ timeout: 200 }}>
                                <IconButton
                                    size="small"
                                    aria-label="Open in full screen"
                                    data-testid="lead-detail-open-fullscreen"
                                    onClick={() => setDetailDialog('fullscreen')}
                                    sx={{ color: '#919EAB', '&:hover': { color: '#637381', bgcolor: 'rgba(145,158,171,0.08)' } }}
                                >
                                    <Iconify icon="mdi:fullscreen" width={18} height={18} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    <Tooltip title="Close overview" TransitionComponent={Zoom} TransitionProps={{ timeout: 200 }}>
                        <IconButton
                            size="small"
                            aria-label="Close overview"
                            data-testid="overview-close"
                            onClick={() => handleChange('', '')}
                            sx={{ color: '#919EAB', '&:hover': { color: '#637381', bgcolor: 'rgba(145,158,171,0.08)' } }}
                        >
                            <Iconify icon="eva:close-fill" width={20} height={20} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Scrollable timeline content */}
            {topSlot}
            <Box sx={{ flex: 1, overflow: 'auto', p: '16px 20px 16px 16px' }}>
                <Box sx={{ position: 'relative' }}>

                    {/* ── Treatment (context — always at top) ── */}
                    {treatmentTabData && treatmentTabData.length > 0 && treatmentTabData.map((item, index) => {
                        const sc = getStatusColor(item.status);
                        const isLastTreatment = index === treatmentTabData.length - 1;
                        const hasMore = hasChecklist || hasEvents;
                        return (
                            <Box key={`treatment-${index}`} sx={timelineItem}>
                                {(!isLastTreatment || hasMore) && <Box sx={timelineLine} />}
                                <Box sx={timelineDot(sc.color)}>
                                    <Iconify icon="solar:teeth-bold" width={14} height={14} sx={{ color: sc.color }} />
                                </Box>
                                <Box sx={timelineContent}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ ...txtBodyStrong, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                            {item?.treatment_data?.treatment_item?.name}
                                        </Typography>
                                        {editingPrice === index ? (
                                            <input
                                                type="text"
                                                value={priceValue}
                                                onChange={(e) => setPriceValue(e.target.value)}
                                                onKeyDown={async (e) => {
                                                    if (e.key === 'Enter') {
                                                        e.target.blur();
                                                    } else if (e.key === 'Escape') {
                                                        e.stopPropagation();
                                                        setEditingPrice(null);
                                                        setPriceValue('');
                                                    }
                                                }}
                                                onBlur={async () => {
                                                    const newPrice = priceValue.trim();
                                                    if (newPrice && newPrice !== String(item?.treatment_data?.price)) {
                                                        const res = await callApi(`/api/v1/practices/${practiceId}/treatments/${item?.treatment_data?.id}/`, 'PATCH', { price: newPrice });
                                                        if (res?.status === 200 && item?.treatment_data) {
                                                            item.treatment_data.price = newPrice;
                                                        }
                                                    }
                                                    setEditingPrice(null);
                                                    setPriceValue('');
                                                }}
                                                autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                                                style={{
                                                    width: '60px', fontSize: '12px', fontWeight: 600, color: '#919EAB',
                                                    border: 'none', borderBottom: '1px solid #919EAB', outline: 'none',
                                                    background: 'transparent', textAlign: 'right', padding: 0,
                                                }}
                                            />
                                        ) : (
                                            <Typography
                                                sx={{
                                                    ...txtCaptionStrong, color: '#919EAB', flexShrink: 0,
                                                    cursor: 'pointer', borderBottom: '1px dashed transparent',
                                                    display: 'flex', alignItems: 'center', gap: '3px',
                                                    '&:hover': { borderBottomColor: '#919EAB' },
                                                    '&:hover .price-edit-icon': { opacity: 0.4 },
                                                }}
                                                onClick={() => { setEditingPrice(index); setPriceValue(String(item?.treatment_data?.price || '')); }}
                                            >
                                                &#163;{item?.treatment_data?.price}
                                                <Iconify className="price-edit-icon" icon="solar:pen-bold" width={10} height={10} sx={{ opacity: 0, transition: 'opacity 0.15s' }} />
                                            </Typography>
                                        )}
                                    </Box>
                                    {item?.treatment_data?.offers?.filter(o => !o.is_expired)?.map((offer, oi) => (
                                        <Box key={oi} sx={{ mt: 0.75, display: 'flex', alignItems: 'baseline', gap: 0.5, '&:hover .offer-edit-icon': { opacity: 0.3 } }}>
                                            <Iconify icon="bxs:offer" width={12} height={12} sx={{ color: '#00A76F', flexShrink: 0, mt: '1px' }} />
                                            <Typography component="span" sx={{ ...txtCaption, color: '#637381', flex: 1 }}>
                                                <Typography component="span" sx={{ ...txtCaptionStrong, color: '#00A76F' }}>
                                                    {capitalizeFirstLetter(offer?.name)}
                                                </Typography>
                                                {offer?.offer && <Box component="span" sx={{ ml: 0.5, '& p, & h1, & h2, & h3, & h4, & h5, & h6, & blockquote, & span, & strong, & em, & li, & ul, & ol': { margin: 0, padding: 0, fontSize: '11px !important', color: '#637381', lineHeight: '1.4 !important', display: 'inline' } }}>{ReactHtmlParser(offer.offer)}</Box>}
                                            </Typography>
                                            {/* TODO: Navigate to treatment settings for offer editing */}
                                            <Iconify className="offer-edit-icon" icon="solar:pen-bold" width={10} height={10} sx={{ opacity: 0, transition: 'opacity 0.15s', flexShrink: 0, cursor: 'pointer' }} />
                                        </Box>
                                    ))}
                                    {item?.treatment_data?.additional_infos?.filter(ai => ai?.categories?.[0] === "FLOW")?.map((info, ii) => (
                                        <Box key={ii} sx={{ mt: 1, '& p, & h1, & h2, & h3, & h4, & h5, & h6, & blockquote': { margin: 0, padding: 0, fontSize: '11px', color: '#637381', lineHeight: 1.4 } }}>{ReactHtmlParser(info?.text)}</Box>
                                    ))}
                                </Box>
                            </Box>
                        );
                    })}

                    {/* ── Checklist (context — stays pinned after treatment) ── */}
                    {hasChecklist && (
                        <Box sx={timelineItem}>
                            {hasEvents && <Box sx={timelineLine} />}
                            <Box sx={timelineDot(checklistSummary.completed.length === checklistSummary.total ? '#00A76F' : '#FFAB00')}>
                                <Iconify icon="eva:checkmark-square-2-fill" width={14} height={14} sx={{ color: checklistSummary.completed.length === checklistSummary.total ? '#00A76F' : '#FFAB00' }} />
                            </Box>
                            <Box sx={timelineContent}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography sx={txtBodyStrong}>
                                        Checklist
                                    </Typography>
                                    <Typography sx={{
                                        ...txtCaptionStrong,
                                        color: checklistSummary.completed.length === checklistSummary.total ? '#00A76F' : '#212B36',
                                    }}>
                                        {checklistSummary.completed.length}/{checklistSummary.total}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.round((checklistSummary.completed.length / checklistSummary.total) * 100)}
                                    sx={{
                                        height: 3, borderRadius: 2, bgcolor: 'rgba(145, 158, 171, 0.12)',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 2,
                                            bgcolor: checklistSummary.completed.length === checklistSummary.total ? '#00A76F' : 'primary.main',
                                        },
                                    }}
                                />
                                <Stack spacing={0.25} sx={{ mt: 1 }}>
                                    {checklistSummary.completed.map((item) => {
                                        const lower = item.value?.toLowerCase();
                                        const isYes = lower === 'yes';
                                        const isNo = lower === 'no';
                                        return (
                                            <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                                                <Iconify
                                                    icon={isYes ? 'eva:checkmark-circle-2-fill' : isNo ? 'eva:close-circle-fill' : 'eva:checkmark-circle-2-fill'}
                                                    sx={{ color: isYes ? '#00A76F' : isNo ? '#FF5630' : '#FFAB00', width: 14, height: 14, flexShrink: 0 }}
                                                />
                                                <Typography sx={{ ...txtCaptionStrong, color: '#212B36', flex: 1 }} noWrap>{item.label}</Typography>
                                            </Box>
                                        );
                                    })}
                                    {checklistSummary.pending.map((item) => (
                                        <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                                            <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: '1.5px solid #C4CDD5', flexShrink: 0 }} />
                                            <Typography sx={{ ...txtCaption, flex: 1 }} noWrap>{item.label}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Box>
                    )}

                    {/* ── Unified event stream — grouped by date (Today / Yesterday / This week / Earlier) ── */}
                    {(() => {
                        const out = [];
                        let lastGroup = null;
                        groupedStream.forEach((event, idx) => {
                            const isLast = idx === groupedStream.length - 1;
                            const group = getDateGroupLabel(event.date);
                            if (group !== lastGroup) {
                                out.push(
                                    <Box
                                        key={`group-${group}-${idx}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mt: lastGroup === null ? 0 : 1.5,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography sx={{ ...txtSectionLabel, whiteSpace: 'nowrap' }}>
                                            {group}
                                        </Typography>
                                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145,158,171,0.16)' }} />
                                    </Box>
                                );
                                lastGroup = group;
                            }
                            if (event.type === 'activity') {
                                const isLatest = event.data?.id && event.data.id === latestOutboundId;
                                out.push(renderActivity(event.data, isLast, isLatest));
                            } else if (event.type === 'activity-group') {
                                out.push(renderActivityGroup(event, isLast));
                            } else if (event.type === 'task') {
                                out.push(renderTask(event.data, event.isCompleted, isLast));
                            } else if (event.type === 'note') {
                                out.push(renderNote(event.data, isLast));
                            }
                        });
                        return out;
                    })()}

                </Box>
            </Box>

            {/* AI Summary Modal */}
            {aiSummaryModalOpen && selectedAIData && (
                <AISummaryModal
                    open={aiSummaryModalOpen}
                    onClose={handleCloseAISummary}
                    onCloseAll={handleCloseAISummary}
                    onBackToScript={handleCloseAISummary}
                    data={{
                        classification: selectedAIData.classification?.status || 'Unknown',
                        classificationEmoji: selectedAIData.classification?.emoji || '',
                        readinessScore: selectedAIData.classification?.readinessScore || 0,
                        budget: selectedAIData.classification?.budget || '',
                        availability: selectedAIData.classification?.availability || '',
                        intent: selectedAIData.classification?.intent || '',
                        summary: selectedAIData.summary?.aiSummaryText || '',
                        keyQuote: selectedAIData.summary?.keyQuote || '',
                        sentiment: selectedAIData.summary?.sentiment || 'Neutral',
                        highlights: selectedAIData.summary?.highlights || [],
                        concerns: selectedAIData.concerns || [],
                        tags: selectedAIData.tags || [],
                        bookAppointment: selectedAIData.recommendedActions?.bookAppointment || { recommended: false },
                        sendWhatsApp: selectedAIData.recommendedActions?.sendWhatsApp || { recommended: false },
                        sendEmail: selectedAIData.recommendedActions?.sendEmail || { recommended: false },
                        addReminder: selectedAIData.recommendedActions?.addReminder || { recommended: false },
                        nextSteps: selectedAIData.recommendedActions?.nextSteps || [],
                    }}
                    leadName={selectedAIData.leadInfo?.name || ''}
                    leadInitials={selectedAIData.leadInfo?.initials || ''}
                    callType={selectedAIData.callInfo?.callType || 'Outbound'}
                    callStartTime={selectedAIData.callInfo?.callStartTime || ''}
                    callDuration={selectedAIData.callInfo?.callDuration || ''}
                    dataCompleteness={selectedAIData.callInfo?.dataCompleteness || 0}
                    viewMode
                    savedAIData={selectedAIData}
                />
            )}

            {/* Lead Detail dialogs — triggered by the mode icons in the header */}
            {showDetailModes && detailDialog && (
                <Dialog
                    open
                    fullScreen={detailDialog === 'fullscreen'}
                    maxWidth={detailDialog === 'popup' ? 'lg' : false}
                    fullWidth={detailDialog === 'popup'}
                    onClose={() => setDetailDialog(null)}
                    data-testid="lead-detail-dialog"
                >
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, px: POPUP_HEADER_PX, py: POPUP_HEADER_PY,
                        position: 'sticky', top: 0, zIndex: 2,
                        background: POPUP_HEADER_GRADIENT, boxShadow: POPUP_HEADER_SHADOW, flexShrink: 0,
                    }}>
                        <Tooltip title="Back" TransitionComponent={Zoom} TransitionProps={{ timeout: 200 }}>
                            <Button
                                color="inherit"
                                variant="outlined"
                                aria-label="Close lead detail"
                                data-testid="lead-detail-dialog-close"
                                onClick={() => setDetailDialog(null)}
                                startIcon={<Iconify icon="eva:arrow-back-fill" width={18} height={18} />}
                                sx={{ textTransform: 'none', fontWeight: 600, color: '#fff', borderColor: '#FFFFFF', '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.16)' } }}
                            >
                                Back
                            </Button>
                        </Tooltip>
                        <Typography sx={{ ...reminderTitleStyle, flex: 1 }} variant="h6" component="div">Lead detail</Typography>
                    </Box>
                    <DialogContent sx={{ p: 0, pt: { xs: 2, sm: 3 }, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <LeadDetailViewBody
                            leadId={leadId}
                            practiceId={practiceId}
                            embedded
                            onNotFound={() => setDetailDialog(null)}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    )
}

OverviewTab.propTypes = {
    treatmentTabData: PropTypes.array,
    leadId: PropTypes.any,
    handleChange: PropTypes.func,
    user: PropTypes.any,
    LeadNotes: PropTypes.array,
    showDetailModes: PropTypes.bool,
    topSlot: PropTypes.node,
};

OverviewTab.defaultProps = {
    showDetailModes: false,
    topSlot: null,
};


export default OverviewTab
